import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ActionListItem, ListResult, SeasonCounter } from '@tww/shared';
import { ACTION_STATUS_LABELS, fmtNum } from '@tww/shared';
import { api } from '../../api/client';
import { useLookups } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, Pager, type Column } from '../../ui/DataTable';
import { ErrorState, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { useListState } from '../../ui/useListState';
import { ActionCalendar } from './ActionCalendar';
import { ActionCell, ActionProgress, ActionStatusPill, MissingText, fmtRange } from './common';
import { Dropdown } from '../../ui/Dropdown';

export function useSeasons() {
  return useQuery({ queryKey: ['action-seasons'], queryFn: () => api.get<{ items: SeasonCounter[] }>('/actions/seasons').then((r) => r.items), staleTime: 30_000 });
}

/** Aktionsliste und Saisonplanung (Plan 04 Abschnitt 5.2): Saison-Auswahl, Liste oder Kalender, Filter als Auswahlfelder */
export function ActionsListPage() {
  const nav = useNavigate();
  const { can } = useAuth();
  const lookups = useLookups();
  const seasons = useSeasons();
  const [sp, setSp] = useSearchParams();
  const view = sp.get('view') === 'kalender' ? 'kalender' : 'liste';
  const { state, update, setFilter, toggleSort, apiParams } = useListState({ sort: 'date', order: 'asc', pageSize: 100 });
  const season = state.filters.season?.[0] ?? '';
  // Beim ersten Aufruf die laufende Saison vorwählen
  useEffect(() => {
    if (!seasons.data || sp.has('f.season') || sp.get('season') === 'alle') return;
    const cur = seasons.data.find((s) => s.current);
    if (cur) setFilter('season', [cur.key]);
  }, [seasons.data]);
  // Auch in der Kalenderansicht laden: Zähler der Filter und Untertitel kommen aus der Liste
  const q = useQuery({ queryKey: ['actions', apiParams], queryFn: () => api.get<ListResult<ActionListItem>>(`/actions?${apiParams}`), placeholderData: (p) => p });
  const facets = q.data?.facets ?? {};
  const cnt = (key: string, value: string) => (facets[key] ?? []).find((f) => f.value === value)?.count ?? 0;
  const states = lookups.data?.find((l) => l.key === 'states')?.values ?? [];
  const types = lookups.data?.find((l) => l.key === 'action_types')?.values ?? [];
  const setView = (v: 'liste' | 'kalender') => {
    const n = new URLSearchParams(sp);
    if (v === 'liste') n.delete('view');
    else n.set('view', v);
    setSp(n, { replace: true });
  };
  const chooseSeason = (key: string) => {
    const n = new URLSearchParams(sp);
    if (key) {
      n.set('f.season', key);
      n.delete('season');
    } else {
      n.delete('f.season');
      n.set('season', 'alle');
    }
    n.delete('page');
    setSp(n, { replace: true });
  };
  const columns: Column<ActionListItem>[] = [
    { key: 'date', label: 'Datum', sortable: true, width: 120, render: (a) => <span className={a.date ? '' : 'mut'}>{fmtRange(a)}</span> },
    { key: 'code', label: 'Aktion', sortable: true, render: (a) => <ActionCell a={a} sub={[a.type_label, a.state_label, a.participants_planned ? `${a.participants_planned} Teilnehmende` : null, a.trees_planned ? `${fmtNum(a.trees_planned)} Bäume` : null].filter(Boolean).join(' · ')} /> },
    { key: 'forester', label: 'Förster', render: (a) => (a.forester ? <Link className="link" to={`/kontakte/${a.forester.id}`}>{a.forester.display_name}</Link> : <span className="mut">–</span>) },
    { key: 'status', label: 'Status', sortable: true, render: (a) => <ActionStatusPill status={a.status} /> },
    { key: 'progress', label: <span title="Ein Segment je Gewerk; rot umrandet: überfällige Punkte">Fortschritt je Gewerk</span>, sortable: true, render: (a) => (a.status === 'ausgefallen' ? <span className="mut">–</span> : <ActionProgress progress={a.progress} />) },
    { key: 'missing', label: 'Fehlt noch', render: (a) => <MissingText m={a.missing} /> },
  ];
  const activeFilters = ['status', 'type', 'state', 'partner', 'has_open', 'has_overdue'].some((k) => state.filters[k]?.length) || state.q;
  return (
    <>
      <div className="head">
        <div>
          <h1>Aktionen</h1>
          <div className="sub">{q.data ? `${q.data.total} ${q.data.total === 1 ? 'Aktion' : 'Aktionen'}` : 'Saisonplanung'}</div>
        </div>
        <div className="actions">
          <Link className="btn" to="/aktionen/offene-punkte">
            <Icon name="tasks" /> Offene Punkte
          </Link>
          {can('actions', 'write') && (
            <Link className="btn primary" to="/aktionen/neu">
              <Icon name="plus" /> Neue Aktion anlegen
            </Link>
          )}
        </div>
      </div>
      <div className="filterbar">
        <div className="field">
          <label htmlFor="ak-season">Saison</label>
          <Dropdown id="ak-season" value={season} onChange={(v) => chooseSeason(v)} placeholder="Alle Saisons" options={(seasons.data ?? []).map((s) => ({ value: String(s.key), label: `${s.label} (${s.count})` }))} />
        </div>
        <div className="field grow">
          <label htmlFor="ak-q">Suche</label>
          <input id="ak-q" value={state.q} onChange={(e) => update({ q: e.target.value })} placeholder="Kennung, Ort" />
        </div>
        <div className="field">
          <label htmlFor="ak-status">Status</label>
          <Dropdown id="ak-status" value={state.filters.status?.[0] ?? ''} onChange={(v) => setFilter('status', v ? [v] : [])} placeholder="Alle" options={Object.entries(ACTION_STATUS_LABELS).map(([k, l]) => ({ value: String(k), label: `${l} (${cnt('status', k)})` }))} />
        </div>
        <div className="field">
          <label htmlFor="ak-type">Typ</label>
          <Dropdown id="ak-type" value={state.filters.type?.[0] ?? ''} onChange={(v) => setFilter('type', v ? [v] : [])} placeholder="Alle" options={types.map((t) => ({ value: String(t.code), label: `${t.label} (${cnt('type', t.code)})` }))} />
        </div>
        <div className="field">
          <label htmlFor="ak-partner">Partner</label>
          <Dropdown id="ak-partner" value={state.filters.partner?.[0] ?? ''} onChange={(v) => setFilter('partner', v ? [v] : [])} placeholder="Alle" options={(facets.partner ?? []).map((f) => ({ value: String(f.value.split('|')[0]), label: `${f.value.split('|')[1]} (${f.count})` }))} />
        </div>
        <div className="field">
          <label htmlFor="ak-state">Bundesland</label>
          <Dropdown id="ak-state" value={state.filters.state?.[0] ?? ''} onChange={(v) => setFilter('state', v ? [v] : [])} placeholder="Alle" options={(facets.state ?? []).map((f) => ({ value: String(f.value), label: `${states.find((s) => s.code === f.value)?.label ?? f.value} (${f.count})` }))} />
        </div>
        <div className="field">
          <label htmlFor="ak-open">Offene Punkte</label>
          <Dropdown id="ak-open" value={state.filters.has_overdue?.[0] ? 'late' : state.filters.has_open?.[0] ? 'open' : ''} onChange={(v) => update({ filters: { ...state.filters, has_open: v === 'open' ? ['1'] : [], has_overdue: v === 'late' ? ['1'] : [] } })} placeholder="Alle" options={[{ value: 'open', label: `nur mit offenen Punkten (${cnt('open', 'has_open')})` }, { value: 'late', label: `nur mit überfälligen Punkten (${cnt('open', 'has_overdue')})` }]} />
        </div>
        <div className="spacer" />
        <div className="opts">
          <div className="seg" role="group" aria-label="Ansicht">
            <button type="button" className={view === 'liste' ? 'on' : ''} onClick={() => setView('liste')}>
              Liste
            </button>
            <button type="button" className={view === 'kalender' ? 'on' : ''} onClick={() => setView('kalender')}>
              Kalender
            </button>
          </div>
          {can('actions', 'write') && (
            <label>
              <input type="checkbox" checked={state.filters.review_needed?.[0] === '1'} onChange={(e) => setFilter('review_needed', e.target.checked ? ['1'] : [])} /> zu prüfen
            </label>
          )}
          {activeFilters && (
            <button type="button" className="btn sm" onClick={() => update({ q: '', filters: season ? { season: [season] } : {} })}>
              Zurücksetzen
            </button>
          )}
        </div>
      </div>
      {view === 'kalender' ? (
        <ActionCalendar seasonKey={season} seasons={seasons.data ?? []} filters={state.filters} q={state.q} />
      ) : (
        <div className="panel">
          {q.isLoading ? (
            <Skeleton rows={8} />
          ) : q.isError ? (
            <ErrorState error={q.error} retry={() => q.refetch()} />
          ) : (
            <DataTable
              columns={columns}
              rows={q.data!.items}
              sort={state.sort ? { key: state.sort, order: state.order ?? 'asc' } : undefined}
              onSort={toggleSort}
              onRowClick={(a) => nav(`/aktionen/${a.id}`)}
              rowClass={(a) => (a.archived_at ? 'archived' : '')}
              empty="Keine Aktionen in dieser Auswahl"
              footer={<Pager page={q.data!.page} pageSize={q.data!.pageSize} total={q.data!.total} onPage={(p) => update({ page: p })} />}
            />
          )}
        </div>
      )}
    </>
  );
}
